import React, { useState } from "react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { Link } from "react-router-dom";
import RecentPosts from "../components/RecentPosts";

export default function Contact() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [botField, setBotField] = useState(""); // honeypot

  return (
    <>
      <Helmet>
        <title>Contact Free1099Calc - Questions, Feedback & Support</title>
        <meta
          name="description"
          content="Contact the Free1099Calc team with questions about 1099 taxes, calculator feedback, partnership ideas, or technical support. We read every message."
        />
        <meta name="keywords" content="contact free1099calc, 1099 tax questions, calculator support, freelancer tax help, partnership inquiry, feedback" />
        <link rel="canonical" href="https://free1099calc.com/contact" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Contact Free1099Calc - Get Help with 1099 Taxes" />
        <meta property="og:description" content="Have questions about 1099 taxes or the calculator? Contact our team for support, feedback, or partnership opportunities." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://free1099calc.com/contact" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact Free1099Calc" />
        <meta name="twitter:description" content="Questions about 1099 taxes? Contact our team for support and feedback." />
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/85 to-slate-900/90" />
        
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full mb-6">
              <span className="text-blue-300 text-sm font-semibold">💬 Get in Touch</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Contact Us
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8">
              Questions, suggestions, or partnership ideas? Send us a note. We read every message.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg"
              >
                Try Calculator →
              </Link>
              <Link
                to="/faq"
                className="inline-block bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-lg font-semibold transition-colors"
              >
                View FAQ
              </Link>
            </div>
          </div>
        </div>
      </div>

    <main className="mx-auto max-w-3xl px-4 md:px-6 py-8">

      <section className="rounded-2xl border bg-white p-6 md:p-8">
        <form
          name="contact"
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="company"  // honeypot field name
          action="/thank-you"
          onSubmit={() => {
            setBusy(true);
            setErr(null);
          }}
          className="space-y-4"
        >
          {/* Required hidden input for Netlify Forms */}
          <input type="hidden" name="form-name" value="contact" />

          {/* Honeypot (hidden to users) */}
          <div className="hidden">
            <label>
              Don’t fill this out if you’re human:{" "}
              <input
                name="company"
                value={botField}
                onChange={(e) => setBotField(e.target.value)}
              />
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-slate-800">Your name</span>
            <input
              type="text"
              name="name"
              required
              className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-slate-300"
              placeholder="Jane Doe"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-800">Email</span>
            <input
              type="email"
              name="email"
              required
              className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-slate-300"
              placeholder="you@example.com"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-800">Subject</span>
            <input
              type="text"
              name="subject"
              className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-slate-300"
              placeholder="How can we help?"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-800">Message</span>
            <textarea
              name="message"
              required
              rows={6}
              className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-slate-300"
              placeholder="Share details so we can help faster…"
            />
          </label>

          {err && <p className="text-sm text-red-600">{err}</p>}

          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center justify-center rounded-lg border bg-black px-4 py-2 text-white hover:opacity-90 disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send message"}
          </button>

          <p className="text-xs text-slate-500">
            We’ll never sell your data. Submissions are processed by Netlify Forms.
          </p>
        </form>
      </section>

      <RecentPosts />

      {/* Hidden fallback form so Netlify reliably detects it at build-time */}
      <form name="contact" data-netlify="true" hidden>
        <input type="hidden" name="form-name" value="contact" />
        <input name="name" />
        <input name="email" />
        <input name="subject" />
        <textarea name="message" />
      </form>
    </main>
    </>
  );
}
